/* eslint-disable no-param-reassign */
import React, { useState, useEffect } from 'react';
import { Row, Col, Spinner, Button, Accordion, Card } from 'react-bootstrap';
import { withFormik, Field, Form, getIn } from 'formik';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { useAbility } from '@casl/react';
import moment from 'moment';
import { DefaultModal, FormikInput, FormikNumber, FormikSelect, CompactCard } from '../../components';
import { contractTypes, paymentSchedules, gratificationTypes } from './FormOptions';
import previredShowRequest from '../../requests/previreds';
import siiTaxesShowRequest from '../../requests/siiTaxes';
import indexPensionFundsRequest from '../../requests/pensionFunds';
import { camelCaseEmptyStringRecursive, sortByAttribute } from '../../services/utils';
import { AbilityContext } from '../../config/abilityContext';
import { indexEmployeesRequest, stunnerEmployeeRequest } from '../../requests/employees';
import indexHealthPrevisionsRequest from '../../requests/healthPrevisions';
import '../Profile/Dashboard/style.scss';

const weekDays = 7;
const overtimeValue = 1.5;
const monthDays = 30;

const PayrollSimulator = ({ values, setFieldValue, setFieldTouched, errors, touched, setValues }) => {
  const ability = useAbility(AbilityContext);
  const [previred, setPrevired] = useState({});
  const [siiRequest, setSiiRequest] = useState(true);
  const [modalShow, setModalShow] = useState(false);
  const [employeeRequest, setEmployeeRequest] = useState(true);
  const [skipSimulation, setSkipSimulation] = useState(false);
  const [selector, setSelector] = useState([]);
  const [previredRequest, setPreviredRequest] = useState(true);
  const [pensionFunds, setPensionFunds] = useState([]);
  const [siiTaxes, setSiiTaxes] = useState([]);
  const [siiTax, setSiiTax] = useState({});
  const [healthPrevisions, setHealthPrevisions] = useState([]);
  const [redo, setRedo] = useState(false);
  const [accordion, setAccordion] = useState('0');
  const dispatch = useDispatch();
  const {
    baseSalary,
    bonus,
    collation,
    commission,
    contractType,
    discountsTotalAmount,
    gratificationAmount,
    gratificationType,
    grossAmount,
    healthPrevision,
    unitOfAccount,
    firstAgreedAmount,
    secondAgreedAmount,
    agreedQuantity,
    householdAllowance,
    legalDiscounts,
    liquid,
    mobilization,
    otherDiscounts,
    otherNonTaxable,
    overtime,
    overtimeAmount,
    paymentSchedule,
    pensionFund,
    taxableBaseAmount,
    weeklyHours
  } = values;

  const { uf, afpMaxTaxableRent, undefinedContractEmployeeAfc, retirementMaxTaxableRent } = previred;
  const fetchHealthPrevisions = () => {
    const params = {
      sort_column: 'name',
      sort_direction: 'asc',
      paginate: false
    };
    indexHealthPrevisionsRequest({
      dispatch,
      params,
      successCallback: response => {
        const { data } = response.data;
        setHealthPrevisions(data);
      }
    });
  };

  const fetchPrevired = () => {
    previredShowRequest({
      dispatch,
      params: { month: moment().month(), year: moment().year() },
      successCallback: response => setPrevired(camelCaseEmptyStringRecursive(response.data)),
      callback: () => setPreviredRequest(false)
    });
  };

  const fetchSiiTaxes = () => {
    siiTaxesShowRequest({
      dispatch,
      params: { month: moment().month(), year: moment().year() },
      successCallback: response => setSiiTaxes(camelCaseEmptyStringRecursive(response.data)),
      callback: () => setSiiRequest(false)
    });
  };

  const fetchPensionFunds = () => {
    const params = {
      sort_column: 'name',
      sort_direction: 'asc',
      display_length: 100
    };
    indexPensionFundsRequest({
      dispatch,
      params,
      successCallback: response => setPensionFunds(response.data.data)
    });
  };

  const fetchEmployees = () => {
    indexEmployeesRequest({
      dispatch,
      params: { active_staffing: true, is_dt: false, paginate: false },
      successCallback: response => setSelector(camelCaseEmptyStringRecursive(response.data.data)),
      callback: () => setEmployeeRequest(false)
    });
  };

  const getTaxData = (nonTaxable, taxable = undefined, newSiiTax = undefined, legal = undefined) => {
    const validSii = newSiiTax || siiTax;
    const partialTaxable = taxable - legal || parseInt(liquid || 0, 10) + otherDiscounts + legalDiscounts - nonTaxable;
    for (let i = 0; i < 7; i += 1) {
      const { [`bound${i}`]: vBound } = validSii;
      if (vBound?.lowBound <= partialTaxable && partialTaxable <= vBound?.highBound) {
        return [vBound?.factor, parseFloat(vBound?.reduce)];
      }
    }
    return [validSii.bound7?.factor, parseFloat(validSii.bound7?.reduce)];
  };

  const taxableFormula = (nonTaxable, taxRate, reduce, partialLegalSum = 0, healthAmount = 0) =>
    (nonTaxable -
      parseInt(otherDiscounts || 0, 10) -
      parseInt(liquid || 0, 10) -
      healthAmount -
      parseInt(legalDiscounts || 0, 10) +
      reduce) /
    (partialLegalSum + (1 - partialLegalSum) * taxRate - 1);

  const getTaxable = (nonTaxable, taxRate, reduce) => {
    let partialLegalSum = 0;
    const { pensionFundRate, afcRate } = values;
    if (pensionFund) {
      partialLegalSum += pensionFundRate;
      partialLegalSum += afcRate;
    }
    if (healthPrevision === 5) {
      partialLegalSum += 0.07;
      return taxableFormula(nonTaxable, taxRate, reduce, partialLegalSum);
    }
    const healthAmount =
      firstAgreedAmount !== 0 || typeof firstAgreedAmount !== 'undefined' ? firstAgreedAmount * uf * (1 - taxRate) : 0;
    return taxableFormula(nonTaxable, taxRate, reduce, partialLegalSum, healthAmount);
  };

  const baseSalarySimpleGratification = (taxable, gratAmount) =>
    Math.round(
      (taxable - parseInt(commission || 0, 10) - gratAmount - parseInt(bonus || 0, 10)) /
        (1 + (overtime * weekDays * overtimeValue) / (monthDays * weeklyHours))
    ) || 0;

  const getBaseSalary = taxable => {
    if (gratificationType === 'edit') {
      return baseSalarySimpleGratification(taxable, gratificationAmount);
    }
    if (gratificationType === 'no') return baseSalarySimpleGratification(taxable, 0);
    const minSalary = parseFloat(previred.particularMinTaxableRent?.replace('.', ',') || 0);
    const baseGratification = Math.round((minSalary * 4.75) / 12);
    const initialBS = Math.round(
      (taxable - parseInt(commission || 0, 10) - parseInt(bonus || 0, 10)) /
        (1.25 + (overtime * weekDays * overtimeValue) / (monthDays * weeklyHours))
    );
    if (baseGratification > initialBS / 4) {
      return initialBS || 0;
    }

    return baseSalarySimpleGratification(taxable, baseGratification);
  };

  const updateGratification = base => {
    const minSalary = parseFloat(previred.particularMinTaxableRent?.replace('.', ',') || 0);
    if (gratificationType === 'yes') {
      setFieldValue('gratificationAmount', Math.round(Math.min((minSalary * 4.75) / 12, parseInt(base || 0, 10) / 4)));
    } else if (gratificationType === 'no') {
      setFieldValue('gratificationAmount', 0);
    }
  };

  const updateOvertime = base => {
    const newOvertime = (parseFloat(overtime, 10) * base * weekDays * overtimeValue) / (monthDays * weeklyHours);
    setFieldValue('overtimeAmount', newOvertime || 0);
  };

  const payrollSimulation = () => {
    if (skipSimulation) {
      return;
    }
    const { pensionFundRate, afcRate } = values;
    const nonTaxable =
      parseInt(collation, 10) +
      parseInt(mobilization, 10) +
      parseInt(householdAllowance, 10) +
      parseInt(otherNonTaxable, 10);
    const [taxRate, reduce] = getTaxData(nonTaxable);
    const taxable = getTaxable(nonTaxable, taxRate, reduce);
    const newBaseSalary = getBaseSalary(taxable);
    updateGratification(newBaseSalary);
    updateOvertime(newBaseSalary);
    const maxTaxable = afpMaxTaxableRent < taxable ? afpMaxTaxableRent : taxable;
    const afpAmount = Math.round(pensionFundRate * maxTaxable || 0);
    const legalPercentage = Math.round(0.07 * maxTaxable);
    const healthAmount =
      healthPrevision !== 'fonasa' && firstAgreedAmount > 0 ? firstAgreedAmount * uf : legalPercentage;
    const afc = pensionFund !== '' ? afpMaxTaxableRent * afcRate : 0;
    const taxAmount = Math.round((maxTaxable - (afpAmount + healthAmount + afc)) * taxRate) - reduce;
    const discountsLegalTotal = Math.round(afpAmount + legalPercentage + afc + taxAmount);
    const aditionalAmount = Math.round(
      firstAgreedAmount > 0 && legalPercentage < firstAgreedAmount * uf ? Math.abs(healthAmount - legalPercentage) : 0
    );
    setFieldValue('pensionFundAmount', afpAmount || 0);
    setFieldValue('baseSalary', newBaseSalary);
    setFieldValue('taxableBaseAmount', taxable);
    setFieldValue('grossAmount', taxable + nonTaxable || 0);
    setFieldValue('firstAgreedAmount', firstAgreedAmount);
    setFieldValue('healthPrevisionLegal', legalPercentage);
    setFieldValue('tax', taxAmount);
    setFieldValue('afc', afc);
    setFieldValue('discountsLegalTotal', discountsLegalTotal);
    setFieldValue('healthPrevisionAditional', aditionalAmount);
    setFieldValue('discountsTotalAmount', discountsLegalTotal + aditionalAmount + otherDiscounts + legalDiscounts || 0);
  };

  const employeeParams = (newValues, employee) => {
    newValues.baseSalary = employee.baseSalary || 0;
    newValues.contractType = employee.contractType;
    newValues.paymentSchedule = employee.paymentSchedule;
    newValues.collation = employee.collation;
    newValues.bonus = employee.bonus;
    newValues.mobilization = employee.mobilization;
    newValues.pensionFund = employee.pensionFund;
    newValues.afcRate = newValues.contractType === 'undefined_term' ? undefinedContractEmployeeAfc / 100 : 0;
    newValues.overtime = 0;
    newValues.overtimeAmount = 0;
    newValues.unitOfAccount = employee.unitOfAccount || 0;
    newValues.agreedQuantity = employee.agreedQuantity || 0;
    newValues.firstAgreedAmount = employee.firstAgreedAmount;
    newValues.secondAgreedAmount = employee.secondAgreedAmount || 0;
    newValues.otherDiscounts = employee.otherDiscounts;
    newValues.legalDiscounts = employee.legalDiscounts;
    newValues.otherNonTaxable = employee.otherNonTaxable;
    newValues.weeklyHours = employee.weeklyHours || 45;
  };

  const employeeHouseholdAllowance = (newValues, employee) => {
    if (employee.familyAllowanceSection === 'no_family_allowance') {
      newValues.householdAllowance = 0;
    } else {
      const allowance = previred[`section${employee.familyAllowanceSection.toLowerCase()}HouseholdAllowance`] || 0;
      newValues.householdAllowance = Math.round(allowance * employee.familyCharges);
    }
  };

  const employeeGratification = (newValues = {}, employee) => {
    if (employee.gratificationType === 'without_gratification') {
      newValues.gratificationType = 'no';
      newValues.gratificationAmount = 0;
    } else if (employee.gratificationPeriod === 'annually') {
      newValues.gratificationType = 'edit';
      newValues.gratificationAmount = 0;
    } else {
      newValues.gratificationType = 'yes';
      const minSalary = parseFloat(previred.particularMinTaxableRent?.replace('.', ',') || 0);
      newValues.gratificationAmount = Math.round(Math.min((minSalary * 4.75) / 12, newValues.baseSalary / 4));
    }
  };

  const employeePensionFund = (newValues, employee) => {
    if (newValues.pensionFund) {
      newValues.pensionFundRate =
        previred[`${employee.pensionFundName?.toLowerCase()}DependentAfpQuotationRate`] / 100 || 0;
      const taxable = newValues.taxableBaseAmount > afpMaxTaxableRent ? afpMaxTaxableRent : newValues.taxableBaseAmount;
      const afpAmount = Math.round(newValues.pensionFundRate * taxable);
      newValues.pensionFundAmount = afpAmount;
    } else {
      newValues.pensionFundAmount = 0;
      newValues.pensionFundRate = 0;
    }
  };

  const employeeHealthPrevision = (newValues, employee) => {
    const taxable = newValues.taxableBaseAmount > afpMaxTaxableRent ? afpMaxTaxableRent : newValues.taxableBaseAmount;
    if (employee.healthPrevision && employee.healthPrevisionName !== 'FONASA') {
      newValues.healthPrevision = employee.healthPrevision;
      newValues.healthPrevisionLegal = Math.round(taxable * 0.07);
      newValues.healthPrevisionAditional = Math.round(
        employee.firstAgreedAmount ? employee.firstAgreedAmount * uf - newValues.healthPrevisionLegal : 0
      );
    } else {
      newValues.healthPrevision = employee.healthPrevision;
      newValues.healthPrevisionLegal = Math.round(taxable * 0.07);
      newValues.healthPrevisionAditional = 0;
    }
  };

  const getSii = pSchedule => {
    let sii;
    siiTaxes.forEach(item => {
      if (item.siiType === pSchedule) {
        setSiiTax(item);
        sii = item;
      }
    });
    return sii || siiTaxes[0];
  };

  const checkPrivateRole = employee => {
    if (!employee.privateRole) {
      return;
    }
    if (ability.cannot('private_role', 'Employee')) {
      setModalShow(true);
    }
  };

  const updateEmployeeData = employee => {
    const newValues = {};
    setSkipSimulation(true);
    employeeParams(newValues, employee);
    employeeHouseholdAllowance(newValues, employee);
    employeeGratification(newValues, employee);
    newValues.taxableBaseAmount = newValues.baseSalary + newValues.gratificationAmount + newValues.bonus;
    const maxTaxableToAfc =
      newValues.taxableBaseAmount > retirementMaxTaxableRent ? retirementMaxTaxableRent : newValues.taxableBaseAmount;
    employeePensionFund(newValues, employee);
    employeeHealthPrevision(newValues, employee);
    const sii = getSii(newValues.paymentSchedule);
    newValues.afc = newValues.pensionFund !== '' ? Math.round(maxTaxableToAfc * newValues.afcRate) : 0;
    let legal = newValues.pensionFundAmount + newValues.healthPrevisionLegal + newValues.afc;
    const [taxRate, reduce] = getTaxData(0, newValues.taxableBaseAmount, sii, legal);
    newValues.tax = Math.round((newValues.taxableBaseAmount - legal) * taxRate - reduce);
    const nonTaxable =
      newValues.collation + newValues.mobilization + newValues.otherNonTaxable + newValues.householdAllowance;
    newValues.grossAmount = newValues.taxableBaseAmount + nonTaxable || 0;
    legal += newValues.tax + newValues.legalDiscounts;
    newValues.discountsLegalTotal = legal;
    newValues.discountsTotalAmount =
      newValues.otherDiscounts + newValues.healthPrevisionAditional + newValues.discountsLegalTotal || 0;
    newValues.liquid = newValues.taxableBaseAmount + nonTaxable - newValues.discountsTotalAmount || 0;

    setValues({ ...newValues });
    setSkipSimulation(false);
    checkPrivateRole(employee);
  };

  const handleSelector = () => {
    if (values.employeeSelector === '') {
      return;
    }
    setEmployeeRequest(true);
    stunnerEmployeeRequest(values.employeeSelector, {
      dispatch,
      successCallback: response => updateEmployeeData(camelCaseEmptyStringRecursive(response.data)),
      callback: () => setEmployeeRequest(false)
    });
  };

  const updateSiiTax = () => {
    if (skipSimulation) {
      return;
    }

    siiTaxes.forEach(item => {
      if (item.siiType === paymentSchedule) {
        setSiiTax(item);
      }
    });
  };

  useEffect(fetchEmployees, []);
  useEffect(fetchPensionFunds, []);
  useEffect(fetchPrevired, []);
  useEffect(fetchSiiTaxes, []);
  useEffect(updateSiiTax, [paymentSchedule, siiTaxes]);
  useEffect(fetchHealthPrevisions, []);
  useEffect(payrollSimulation, [
    bonus,
    collation,
    commission,
    contractType,
    gratificationAmount,
    healthPrevision,
    householdAllowance,
    legalDiscounts,
    liquid,
    unitOfAccount,
    firstAgreedAmount,
    secondAgreedAmount,
    agreedQuantity,
    mobilization,
    otherDiscounts,
    otherNonTaxable,
    pensionFund,
    overtime,
    redo,
    weeklyHours
  ]);

  const onRequest = siiRequest || previredRequest || employeeRequest;

  return (
    <Form>
      <Row className="mt-4 mb-4">
        {onRequest && (
          <div className="containerSpinnerLoad center-spinner ml-n2 ml-lg-n0 w-100">
            <Spinner className="mb-4" animation="border" variant="primary" />
          </div>
        )}
        <Col xs={12}>
          <h2 className="mt-3 mb-3">Simulador de Sueldo</h2>
        </Col>
        <Accordion defaultActiveKey="0" className="w-100 mb-3 px-3">
          <Col className={`${accordion === '0' ? 'top-header-green' : 'top-header-light-gray'} br-5 mt-2`}>
            <Accordion.Toggle
              as={Col}
              eventKey="0"
              onClick={() => setAccordion('0')}
              className="card-header-title text-uppercase"
            >
              Datos Generales
            </Accordion.Toggle>
          </Col>
          <Accordion.Collapse eventKey="0">
            <Card.Body className="div-content payroll-simulator">
              <Row>
                <Col md={2}>
                  <FormikNumber
                    disabled
                    leftAddon="$"
                    label="UF:"
                    value={uf || 0}
                    decimalSeparator=","
                    thousandSeparator="."
                    labelClass="mb-1"
                  />
                </Col>
                <Col md={3}>
                  <FormikInput disabled label="Fecha UF:" value={previred.ufDate || ''} labelClass="mb-1" />
                </Col>
                <Col md={5}>
                  <Field name="employeeSelector">
                    {({ field }) => (
                      <FormikSelect
                        {...field}
                        margin="mb-0"
                        label="Trabajador:"
                        labelClass="mb-1"
                        placeholder="Seleccionar Trabajador"
                        options={sortByAttribute(selector, 'fullName')}
                        onChange={data => setFieldValue(field.name, data ? data.value : '')}
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button
                    variant="primary"
                    disabled={values.employeeSelector === '' || onRequest}
                    onClick={handleSelector}
                    className="mb-3 w-100"
                    size="sm"
                  >
                    AGREGAR
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>

          <Col className={`${accordion === '1' ? 'top-header-green' : 'top-header-light-gray'} br-5 mt-2`}>
            <Accordion.Toggle
              as={Col}
              eventKey="1"
              onClick={() => setAccordion('1')}
              className="card-header-title text-uppercase"
            >
              Sueldo Objetivo
            </Accordion.Toggle>
          </Col>
          <Accordion.Collapse eventKey="1">
            <Card.Body className="div-content">
              <Row>
                <Col md={3}>
                  <Field name="parsedLiquid">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        abbr
                        leftAddon="$"
                        fieldName="liquid"
                        value={liquid}
                        label="Sueldo Líquido Objetivo:"
                        onValueChange={data => setFieldValue('liquid', data?.floatValue || 0)}
                        labelClass="mb-1"
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="parsedBaseSalary">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        disabled
                        leftAddon="$"
                        fieldName="baseSalary"
                        value={baseSalary}
                        label="Sueldo Base:"
                        labelClass="mb-1"
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="parsedGrossAmount">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        disabled
                        leftAddon="$"
                        fieldName="grossAmount"
                        value={grossAmount}
                        label="Sueldo Bruto:"
                        labelClass="mb-1"
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="paymentSchedule">
                    {({ field }) => (
                      <FormikSelect
                        {...field}
                        abbr
                        label="Contrato:"
                        labelClass="mb-1"
                        placeholder="Seleccionar contrato"
                        options={paymentSchedules}
                        defaultValue={values.paymentSchedule}
                        onChange={data => setFieldValue(field.name, data ? data.value : '')}
                        setFieldTouched={() => setFieldTouched(field.name)}
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                      />
                    )}
                  </Field>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md={3}>
                  <Field name="contractType">
                    {({ field }) => (
                      <FormikSelect
                        {...field}
                        abbr
                        label="Tipo de Contrato:"
                        labelClass="mb-1"
                        placeholder="Seleccionar plazo contrato"
                        options={contractTypes}
                        defaultValue={values.contractType}
                        onChange={data => {
                          setFieldValue(field.name, data ? data.value : '');
                          setFieldValue('afcRate', data.value === 'undefined_term' ? 0.006 : 0);
                        }}
                        setFieldTouched={() => setFieldTouched(field.name)}
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="weeklyHours">
                    {({ field }) => (
                      <FormikInput
                        {...field}
                        abbr
                        inputType="number"
                        label="Horas Semanales:"
                        labelClass="mb-1"
                        placeholder="Ingresar horas semanales"
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                      />
                    )}
                  </Field>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>

          <Col className={`${accordion === '2' ? 'top-header-green' : 'top-header-light-gray'} br-5 mt-2`}>
            <Accordion.Toggle
              as={Col}
              eventKey="2"
              onClick={() => setAccordion('2')}
              className="card-header-title text-uppercase"
            >
              Haberes
            </Accordion.Toggle>
          </Col>
          <Accordion.Collapse eventKey="2">
            <Card.Body className="div-content">
              <h6 className="font-weight-bold mb-3">Imponibles</h6>
              <Row>
                <Col md={3}>
                  <Field name="parsedBaseSalary">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        disabled
                        leftAddon="$"
                        fieldName="baseSalary"
                        value={baseSalary}
                        label="Sueldo Base:"
                        labelClass="mb-1"
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="gratificationType">
                    {({ field }) => (
                      <FormikSelect
                        {...field}
                        abbr
                        label="Gratificación:"
                        labelClass="mb-1"
                        placeholder="Seleccionar gratificación"
                        tooltipText="Al incluir, automáticamente se calcula el menor entre el 25% del sueldo base o
                                     4,75 sueldos mínimos mensuales y mensualizado"
                        options={gratificationTypes}
                        defaultValue={values.gratificationType}
                        onChange={data => {
                          setFieldValue(field.name, data ? data.value : '');
                          if (data.value === 'yes') {
                            const minSalary = parseFloat(previred.particularMinTaxableRent?.replace('.', ',') || 0);
                            setFieldValue(
                              'gratificationAmount',
                              Math.round(Math.min((minSalary * 4.75) / 12, parseInt(baseSalary || 0, 10) / 4))
                            );
                          } else if (data.value === 'no') {
                            setFieldValue('gratificationAmount', 0);
                          }
                        }}
                        setFieldTouched={() => setFieldTouched(field.name)}
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="parsedGratificationAmount">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        disabled={gratificationType !== 'edit'}
                        leftAddon="$"
                        fieldName="gratificationAmount"
                        label="Monto Gratificación:"
                        labelClass="mb-1"
                        value={gratificationAmount}
                        onValueChange={data => setFieldValue('gratificationAmount', data?.floatValue || 0)}
                        onBlur={() => setRedo(!redo)}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="parsedOvertime">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        rightAddon="Hrs"
                        fieldName="overtime"
                        label="Cantidad Horas Extras:"
                        labelClass="mb-1"
                        decimalScale={2}
                        value={overtime}
                        onValueChange={data => setFieldValue('overtime', data?.floatValue || 0)}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="parsedOvertimeAmount">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        disabled
                        leftAddon="$"
                        fieldName="overtimeAmount"
                        value={overtimeAmount}
                        label="Monto Horas Extras:"
                        labelClass="mb-1"
                        onValueChange={data => setFieldValue('overtimeAmount', data?.floatValue || 0)}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="parsedCommission">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        leftAddon="$"
                        fieldName="commission"
                        label="Monto Comisiones:"
                        labelClass="mb-1"
                        value={values.commission}
                        onValueChange={data => setFieldValue('commission', data?.floatValue || 0)}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="parsedBonus">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        leftAddon="$"
                        setFieldValue={setFieldValue}
                        fieldName="bonus"
                        label="Monto Bonos:"
                        labelClass="mb-1"
                        value={bonus}
                        onValueChange={data => setFieldValue('bonus', data?.floatValue || 0)}
                      />
                    )}
                  </Field>
                </Col>
              </Row>
              <hr />
              <h6 className="font-weight-bold mb-3">No Imponibles</h6>
              <Row>
                <Col md={3}>
                  <Field name="parsedCollation">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        leftAddon="$"
                        fieldName="collation"
                        label="Colación:"
                        labelClass="mb-1"
                        value={collation}
                        onValueChange={data => setFieldValue('collation', data?.floatValue || 0)}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="parsedMobilization">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        leftAddon="$"
                        fieldName="mobilization"
                        label="Movilización:"
                        labelClass="mb-1"
                        value={mobilization}
                        onValueChange={data => setFieldValue('mobilization', data?.floatValue || 0)}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="parsedHouseholdAllowance">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        leftAddon="$"
                        fieldName="householdAllowance"
                        value={householdAllowance}
                        label="Asignación Familiar:"
                        labelClass="mb-1"
                        onValueChange={data => setFieldValue('householdAllowance', data?.floatValue || 0)}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="parsedOtherNonTaxable">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        leftAddon="$"
                        fieldName="otherNonTaxable"
                        value={otherNonTaxable}
                        label="Otros:"
                        labelClass="mb-1"
                        onValueChange={data => setFieldValue('otherNonTaxable', data?.floatValue || 0)}
                      />
                    )}
                  </Field>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>

          <Col className={`${accordion === '3' ? 'top-header-green' : 'top-header-light-gray'} br-5 mt-2`}>
            <Accordion.Toggle
              as={Col}
              eventKey="3"
              onClick={() => setAccordion('3')}
              className="card-header-title text-uppercase"
            >
              Descuentos
            </Accordion.Toggle>
          </Col>
          <Accordion.Collapse eventKey="3">
            <Card.Body className="div-content">
              <h6 className="font-weight-bold mb-3">Legales</h6>
              <Row>
                <Col md={3}>
                  <Field name="pensionFund">
                    {({ field }) => (
                      <FormikSelect
                        {...field}
                        abbr
                        isClearable
                        label="AFP:"
                        labelClass="mb-1"
                        placeholder="No Cotiza"
                        options={pensionFunds}
                        defaultValue={values.pensionFund}
                        onChange={data => {
                          setFieldValue(field.name, data ? data.value : '');
                          if (!data) {
                            setFieldValue('pensionFundRate', 0);
                            setFieldValue('pensionFundAmount', 0);
                          } else {
                            const rate = previred[`${data.label.toLowerCase()}DependentAfpQuotationRate`] / 100;
                            const maxTaxableRentAfp = previred[`${data.label.toLowerCase()}afpMaxTaxableRent`];
                            const amount =
                              taxableBaseAmount > maxTaxableRentAfp ? maxTaxableRentAfp : taxableBaseAmount;
                            setFieldValue('pensionFundRate', rate || 0);
                            setFieldValue('pensionFundAmount', rate * amount || 0);
                          }
                        }}
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="parsedPensionFundAmount">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        leftAddon="$"
                        disabled
                        fieldName="pensionFundAmount"
                        onValueChange={data => {
                          setFieldValue('pensionFundAmount', data?.floatValue || 0);
                        }}
                        value={values.pensionFundAmount}
                        label="Monto AFP:"
                        labelClass="mb-1"
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="healthPrevision">
                    {({ field }) => (
                      <FormikSelect
                        {...field}
                        abbr
                        label="Previsión:"
                        labelClass="mb-1"
                        placeholder="Seleccionar previsión"
                        options={healthPrevisions}
                        defaultValue={values.healthPrevision}
                        onChange={data => {
                          setFieldValue('healthPrevision', data ? data.value : '');
                          if (data.value === 5) {
                            setFieldValue('firstAgreedAmount', 0);
                            setFieldValue('healthPrevisionAditional', 0);
                          }
                        }}
                        error={getIn(errors, field.name)}
                        touched={getIn(touched, field.name)}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="healthPrevisionLegal">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        leftAddon="$"
                        disabled
                        fieldName="healthPrevisionLegal"
                        onValueChange={data => {
                          setFieldValue('healthPrevisionLegal', data?.floatValue || 0);
                        }}
                        value={values.healthPrevisionLegal || 0}
                        label="Monto Previsión (7% Legal):"
                        labelClass="mb-1"
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="parsedAfc">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        leftAddon="$"
                        disabled
                        fieldName="afc"
                        label="Seguro de Cesantía:"
                        labelClass="mb-1"
                        value={values.afc}
                        onValueChange={data => {
                          setFieldValue('afc', data?.floatValue || 0);
                        }}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="parsedTax">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        leftAddon="$"
                        disabled
                        fieldName="tax"
                        label="Impuesto:"
                        labelClass="mb-1"
                        value={values.tax}
                        onValueChange={data => setFieldValue('tax', data?.floatValue || 0)}
                      />
                    )}
                  </Field>
                </Col>
                <Col md={3}>
                  <Field name="discountsLegalTotal">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        leftAddon="$"
                        fieldName="discountsLegalTotal"
                        onValueChange={data => {
                          setFieldValue('discountsLegalTotal', data?.floatValue || 0);
                        }}
                        value={values.discountsLegalTotal}
                        label="Total Descuentos Legales:"
                        labelClass="mb-1"
                      />
                    )}
                  </Field>
                </Col>
              </Row>
              <hr />
              <h6 className="font-weight-bold mb-3">Adicional</h6>
              <Row>
                <Col md={3}>
                  <Field name="firstAgreedAmount">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        leftAddon="UF"
                        disabled={healthPrevision === 5}
                        fieldName="firstAgreedAmount"
                        label="Monto Pactado (Isapre):"
                        labelClass="mb-1"
                        value={firstAgreedAmount}
                        defaultValue={values.firstAgreedAmount || 0}
                        decimalSeparator=","
                        decimalScale={2}
                        onValueChange={data => {
                          setFieldValue('firstAgreedAmount', data?.floatValue || 0);
                        }}
                      />
                    )}
                  </Field>
                </Col>

                <Col md={3}>
                  <Field name="healthPrevisionAditional">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        leftAddon="$"
                        disabled={healthPrevision === 5}
                        fieldName="healthPrevisionAditional"
                        value={values.healthPrevisionAditional}
                        onValueChange={data => {
                          setFieldValue('healthPrevisionAditional', data?.floatValue || 0);
                        }}
                        label="Previsión Adicional:"
                        labelClass="mb-1"
                      />
                    )}
                  </Field>
                </Col>
              </Row>
              <hr />
              <h6 className="font-weight-bold mb-3">Otros Descuentos</h6>
              <Row>
                <Col md={3}>
                  <Field name="parsedOtherDiscounts">
                    {({ field }) => (
                      <FormikNumber
                        {...field}
                        setFieldValue={setFieldValue}
                        leftAddon="$"
                        fieldName="otherDiscounts"
                        label="Otros:"
                        labelClass="mb-1"
                        value={otherDiscounts}
                        onValueChange={data => setFieldValue('otherDiscounts', data?.floatValue || 0)}
                      />
                    )}
                  </Field>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>
        </Accordion>

        <Col md={4} xs={12} className="px-3 mb-3">
          <CompactCard
            variant="success"
            label="Total Haberes"
            title={`$ ${parseInt(grossAmount, 10)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`}
            customSize="h5"
            paddingTitle="pt-0"
            paddingContent
            active
          />
        </Col>
        <Col md={4} xs={12} className="px-3 mb-3">
          <CompactCard
            variant="danger"
            label="Total Descuento"
            title={`$ ${parseInt(discountsTotalAmount, 10)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`}
            customSize="h5"
            paddingTitle="pt-0"
            paddingContent
            active
          />
        </Col>
        <Col md={4} xs={12} className="px-3 mb-3">
          <CompactCard
            variant="info-grey"
            label="Sueldo Liquido"
            title={`$ ${parseInt(liquid, 10)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`}
            customSize="h5"
            paddingTitle="pt-0"
            paddingContent
            active
          />
        </Col>
      </Row>
      <DefaultModal
        title="Rol Privado"
        body="Este trabajador tiene rol privado y no posees los permisos necesarios para ver su sueldo base."
        show={modalShow}
        handleClose={() => setModalShow(false)}
        titleBtnClose="Cerrar"
        withConfirm={false}
      />
    </Form>
  );
};

const setInitialValues = () => {
  return {
    afcRate: 0.006,
    baseSalary: 0,
    bonus: 0,
    collation: 0,
    commission: 0,
    contractType: 'undefined_term',
    discountsTotalAmount: 0,
    employeeSelector: '',
    gratificationAmount: 0,
    gratificationType: 'yes',
    grossAmount: 0,
    healthPrevision: 'fonasa',
    firstAgreedAmount: 0,
    householdAllowance: 0,
    legalDiscounts: 0,
    liquid: 0,
    mobilization: 0,
    otherDiscounts: 0,
    otherNonTaxable: 0,
    overtime: 0,
    overtimeAmount: 0,
    paymentSchedule: 'monthly',
    pensionFund: '',
    pensionFundRate: 0,
    tax: 0,
    taxableBaseAmount: 0,
    weeklyHours: 45
  };
};

const validationSchema = Yup.object().shape({
  afcRate: Yup.number(),
  baseSalary: Yup.number(),
  bonus: Yup.number(),
  collation: Yup.number(),
  commission: Yup.number(),
  contractType: Yup.string(),
  discountsTotalAmount: Yup.number(),
  gratificationAmount: Yup.number(),
  gratificationType: Yup.string(),
  grossAmount: Yup.number(),
  healthPrevision: Yup.string(),
  firstAgreedAmount: Yup.number(),
  householdAllowance: Yup.number(),
  legalDiscounts: Yup.number(),
  liquid: Yup.number(),
  mobilization: Yup.number(),
  otherDiscounts: Yup.number(),
  otherNonTaxable: Yup.number(),
  overtime: Yup.number(),
  overtimeAmount: Yup.number(),
  paymentSchedule: Yup.string(),
  pensionFundAmount: Yup.number(),
  pensionFundRate: Yup.number(),
  tax: Yup.number(),
  taxableBaseAmount: Yup.number(),
  weeklyHours: Yup.number()
    .positive('Debe ser mayor a 0')
    .max(45, 'Debe ser menor o igual a 45')
    .required('Debes ingresar un número de horas')
});

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  enableReinitialize: true,
  validateOnMount: props => props.action !== 'new'
})(PayrollSimulator);
